/**
 * F 322 — PART 1: THE TELEMETRY RETENTION SETTING.
 *
 * A telemetry signal's retention decision is a SETTING, like every other
 * setting, so it is a node in the graph and every app inheriting FCr gets it.
 * This file is the mechanism; it does not decide which signals matter or what
 * their windows are. FVC decides that. We build the thing they decide with.
 *
 * WHY A `Setting` AND NOT A NEW EntityKind
 *
 * `Setting.value` is `unknown` and `valueType` includes `'json'`, for which
 * `valueMatchesType` returns true unconditionally. A structured payload is
 * therefore already legal, expressible, and round-trippable through the
 * existing kind. Adding a `telemetry` kind would be a core edit for no gain.
 *
 * WHY THE SCOPE LADDER IS THE OVERRIDE
 *
 * `SettingStore` already resolves `user > app > instance > global` and then the
 * entity's own `default`. "The user can override any time" is not a feature
 * this file adds — it is behaviour that was already there. The signal's
 * preference lives at `global`; an override lives at `user`.
 *
 * WHY THE COST IS DERIVED AND NEVER STORED
 *
 * The operator's rule is that the cost is shown BEFORE the override is
 * accepted. That means it must be derivable from the mode alone, with no
 * storage and no face, so no face can show a stale or invented figure.
 */

import type { Setting, SettingScope } from '../../types/models/Setting';
import { settingFcid, settingId, valueMatchesType } from '../../types/models/Setting';
import type { SettingStore } from '../settings/SettingStore';

// ── the three modes ────────────────────────────────────────────────────────

/**
 * `live-only`  the default. The reading exists only while the face is open.
 * `rolled-up`  samples are folded into a bucket; the face reads buckets.
 * `kept`       every reading is retained until something deletes it.
 *
 * Keeping is DELIBERATE, not a power-user option: it is the only mode whose
 * cost is unbounded, so it is the one that has to be chosen on purpose.
 */
export type RetentionMode = 'live-only' | 'rolled-up' | 'kept';

export const RETENTION_MODES: readonly RetentionMode[] = ['live-only', 'rolled-up', 'kept'];

/** The default is live-only. Stated once so nothing can drift from it. */
export const DEFAULT_RETENTION_MODE: RetentionMode = 'live-only';

/** Window strings the mechanism accepts. FVC picks which one; we only name it. */
export const ROLLUP_WINDOWS: readonly string[] = ['1m', '5m', '1h', '6h', '1d', '1w'];

/** Setting keys are namespaced so telemetry never collides with an app's own. */
export const TELEMETRY_SETTING_PREFIX = 'telemetry.';

/** The scope a signal's own preference is declared at. Overrides sit above it. */
export const TELEMETRY_DEFAULT_SCOPE: SettingScope = 'global';

// ── the payload a telemetry setting carries ────────────────────────────────

export interface TelemetryRetentionValue {
  /** The signal this is about. FVC decides which signals exist. */
  signal: string;
  mode: RetentionMode;
  /**
   * Only meaningful for `rolled-up`. Null for `live-only` and `kept` — a
   * window on a mode that does not bucket is a value nobody reads, and leaving
   * it set is how a setting starts lying.
   */
  window: string | null;
  /**
   * What this mode costs, in words. Null for `live-only`, which is the default
   * and therefore the one with nothing to warn about.
   */
  statedCost: string | null;
}

/** The cost of each mode, derived — never stored, so it cannot go stale. */
export function retentionCost(mode: RetentionMode, window: string | null): string | null {
  switch (mode) {
    case 'live-only':
      return null;
    case 'rolled-up':
      return window
        ? `readings older than ${window} are folded into buckets; the raw sample is discarded`
        : 'readings are folded into buckets; no window set, so the bucket width is unstated';
    case 'kept':
      return 'every reading is kept until something deletes it; nothing in the store does that on a schedule';
  }
}

/** A retention value with every field derived rather than asserted. */
export function makeRetentionValue(
  signal: string,
  mode: RetentionMode = DEFAULT_RETENTION_MODE,
  window: string | null = null,
): TelemetryRetentionValue {
  const win = mode === 'rolled-up' ? window : null;
  return { signal, mode, window: win, statedCost: retentionCost(mode, win) };
}

// ── validation ─────────────────────────────────────────────────────────────

export function isRetentionMode(v: unknown): v is RetentionMode {
  return typeof v === 'string' && (RETENTION_MODES as readonly string[]).includes(v);
}

/**
 * True when `v` is a usable telemetry retention value. A stored value that
 * fails this is not a retention decision and must not be acted on.
 */
export function isRetentionValue(v: unknown): v is TelemetryRetentionValue {
  if (!v || typeof v !== 'object') return false;
  const r = v as Partial<TelemetryRetentionValue>;
  return (
    typeof r.signal === 'string' &&
    r.signal.length > 0 &&
    isRetentionMode(r.mode) &&
    (r.window === null || typeof r.window === 'string') &&
    (r.statedCost === null || typeof r.statedCost === 'string')
  );
}

// ── the setting node ───────────────────────────────────────────────────────

export function telemetrySettingKey(signal: string): string {
  return `${TELEMETRY_SETTING_PREFIX}${signal}`;
}

export function isTelemetrySettingKey(key: string): boolean {
  return key.startsWith(TELEMETRY_SETTING_PREFIX);
}

/**
 * Build the Setting node for a signal.
 *
 * `value` is the signal's own preference. `default` is always the live-only
 * fallback, so a user who has never opened the signal still resolves to a
 * value rather than `undefined`.
 */
export function makeTelemetrySetting(input: {
  signal: string;
  id?: string;
  mode?: RetentionMode;
  window?: string | null;
  scope?: SettingScope;
  scopeId?: string | null;
  label?: string;
  description?: string;
  createdAt?: string;
}): Setting {
  const { signal } = input;
  if (!signal || typeof signal !== 'string' || signal.length === 0) {
    throw new Error('makeTelemetrySetting: signal is required');
  }
  const scope = input.scope ?? TELEMETRY_DEFAULT_SCOPE;
  const scopeId = input.scopeId ?? null;
  const key = telemetrySettingKey(signal);
  const id = input.id ?? settingId(key, scope, scopeId);
  const value = makeRetentionValue(signal, input.mode ?? DEFAULT_RETENTION_MODE, input.window ?? null);
  const now = input.createdAt ?? new Date().toISOString();

  return {
    id,
    fcid: settingFcid(id),
    key,
    label: input.label ?? `Telemetry retention — ${signal}`,
    value,
    valueType: 'json',
    scope,
    scopeId,
    default: makeRetentionValue(signal, DEFAULT_RETENTION_MODE, null),
    description: input.description ?? `How long readings for "${signal}" are kept. Default: live-only.`,
    editable: true,
    createdAt: now,
    updatedAt: now,
  };
}

// ── reading the decision back out ──────────────────────────────────────────

/**
 * Resolve a signal's effective retention.
 *
 * Walks the scope ladder through SettingStore, so an override at `user` beats
 * the signal's preference at `global`, and the entity's `default` beats
 * `undefined`. A value that is present but malformed is IGNORED, not
 * half-read: retention is a decision about deleting data, so a broken
 * decision must resolve to the safe default rather than to `undefined`.
 */
export function resolveRetention(
  store: SettingStore,
  signal: string,
  scope?: SettingScope,
  scopeId?: string | null,
): TelemetryRetentionValue {
  const fallback = makeRetentionValue(signal, DEFAULT_RETENTION_MODE, null);
  const key = telemetrySettingKey(signal);

  // NOTE: a valid stored value is RE-DERIVED, never returned verbatim.
  // `isRetentionValue` checks shape, not truth — a hand-written value can carry
  // a `statedCost` that contradicts its own `mode` ("live-only, free forever").
  // Re-deriving is what makes the cost impossible to forge and impossible to
  // leave stale when a mode's wording changes.
  const adopt = (v: TelemetryRetentionValue): TelemetryRetentionValue =>
    makeRetentionValue(v.signal, v.mode, v.window);

  const raw = store.getSetting(key, scope, scopeId);
  if (isRetentionValue(raw)) return adopt(raw);

  const entity = store.getSettingEntity(key, scope, scopeId);
  if (entity && isRetentionValue(entity.value)) return adopt(entity.value);
  if (entity && isRetentionValue(entity.default)) return adopt(entity.default);

  return fallback;
}

/** The cost a user would be accepting by moving to `mode`. Null for live-only. */
export function overrideCost(mode: RetentionMode, window: string | null = null): string | null {
  return retentionCost(mode, mode === 'rolled-up' ? window : null);
}

/**
 * The consequence of turning a signal OFF — stated, because "off" is not the
 * absence of a decision, it is a decision to stop writing.
 */
export function offConsequence(signal: string): string {
  return `"${signal}" stops being written. Existing readings are NOT removed by turning it off; only a purge does that, and nothing in the store purges on a schedule.`;
}

/** True when `setting` is a telemetry retention setting this module wrote. */
export function isTelemetrySetting(setting: Setting | null | undefined): boolean {
  if (!setting) return false;
  if (!isTelemetrySettingKey(setting.key)) return false;
  if (!valueMatchesType(setting.value, 'json')) return false;
  return isRetentionValue(setting.value);
}

/** Every telemetry setting in a store, for a settings face to render. */
export function listTelemetrySettings(store: SettingStore): Setting[] {
  return store.allSettings().filter(isTelemetrySetting);
}
